import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signInWithCredential,
    GoogleAuthProvider,
    sendEmailVerification,
    sendPasswordResetEmail,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    User,
    UserCredential,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebaseConfig';

import { UserProfile, UserRole } from '../types';
import { safeLocalStorage } from '../utils/safeStorage';




/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (
    email: string,
    password: string,
    displayName: string,
    role: UserRole = 'participant'
): Promise<UserCredential> => {
    // In mock mode, we could just simulate success, but standard use is logging in existing mock users

    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // Password accounts start unverified, and claimPendingProvision
        // refuses to attach a provisioned role to an unverified email (it
        // would let anyone who guessed the address claim it first). Send the
        // verification link right away; best-effort — a mail failure must
        // not fail the signup.
        sendEmailVerification(userCredential.user).catch(() => {});

        // Create user profile in Firestore
        await createUserProfile(userCredential.user, displayName, role);

        return userCredential;
    } catch (error: any) {
        console.error('Error signing up:', error);
        throw new Error(getAuthErrorMessage(error.code));
    }
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (
    email: string,
    password: string
): Promise<UserCredential> => {

    try {
        const result = await signInWithEmailAndPassword(auth, email, password);

        // Create user profile if missing (mirrors signInWithGoogle pattern)
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        if (!userDoc.exists()) {
            await createUserProfile(result.user, result.user.displayName || email.split('@')[0], 'participant');
        }

        return result;
    } catch (error: any) {
        console.error('Error signing in:', error);
        throw new Error(getAuthErrorMessage(error.code));
    }
};

/**
 * Send a password reset email.
 *
 * Firebase sends this mail itself — NOT SendGrid — so it works while the
 * SendGrid credits are out. With email-enumeration protection enabled the API
 * resolves identically whether or not an account exists for the address, so
 * callers must phrase the confirmation conditionally ("if an account exists")
 * rather than asserting the address is registered.
 */
export const sendPasswordReset = async (email: string): Promise<void> => {
    try {
        await sendPasswordResetEmail(auth, email.trim());
    } catch (error: any) {
        console.error('Error sending password reset:', error);
        throw new Error(getAuthErrorMessage(error.code));
    }
};

/**
 * Sign in with Google via GIS credential exchange.
 * Called from the GIS renderButton callback with the ID token.
 * No Firebase auth handler page — pure Google account picker.
 */
export const signInWithGoogleCredential = async (
    idToken: string,
    role: UserRole = 'participant'
): Promise<UserCredential> => {
    try {
        const credential = GoogleAuthProvider.credential(idToken);
        const result = await signInWithCredential(auth, credential);

        // Check if user profile exists, if not create it
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        if (!userDoc.exists()) {
            await createUserProfile(result.user, result.user.displayName || 'User', role);
        }

        return result;
    } catch (error: any) {
        console.error('Error signing in with Google:', error);
        throw new Error(getAuthErrorMessage(error.code));
    }
};

/**
 * Legacy signInWithGoogle via Firebase popup — fallback if GIS is unavailable.
 */
export const signInWithGoogle = async (
    role: UserRole = 'participant'
): Promise<UserCredential> => {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');

    try {
        const result = await signInWithPopup(auth, provider);

        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        if (!userDoc.exists()) {
            await createUserProfile(result.user, result.user.displayName || 'User', role);
        }

        return result;
    } catch (error: any) {
        const code = error?.code as string | undefined;
        // In-app browsers (WhatsApp, Instagram — see App.tsx join-link handling)
        // commonly block the popup outright, or spawn it and it silently fails
        // to complete, which Firebase reports as 'popup-closed-by-user' (its
        // internal poll only rejects ~10s after the popup opens, so there's no
        // reliable timing signal to distinguish that from a deliberate user
        // cancel — see task-8-report.md). Treated as a block either way; a
        // deliberate cancel may trigger a redirect ~10s later.
        if (
            code === 'auth/popup-blocked' ||
            code === 'auth/operation-not-supported-in-this-environment' ||
            code === 'auth/cancelled-popup-request' ||
            code === 'auth/popup-closed-by-user'
        ) {
            safeLocalStorage.setItem('googleSignInRole', role);
            try {
                await signInWithRedirect(auth, provider);
            } catch (redirectError: any) {
                // Some in-app webviews disable redirect navigation outright —
                // surface this through the same error mapping as every other
                // path instead of letting the raw Firebase error escape.
                console.error('Error initiating Google redirect sign-in:', redirectError);
                throw new Error(getAuthErrorMessage(redirectError?.code));
            }
            // Full-page navigation is underway; handleGoogleRedirectResult()
            // resumes the sign-in on the way back. Never resolves here.
            return new Promise<UserCredential>(() => {});
        }
        console.error('Error signing in with Google:', error);
        throw new Error(getAuthErrorMessage(code));
    }
};

/**
 * Handle the redirect result when the app loads after a Google sign-in redirect.
 * Call this once on app startup.
 */
export const handleGoogleRedirectResult = async (): Promise<UserCredential | null> => {
    try {
        const result = await getRedirectResult(auth);
        if (!result) return null;

        const role = (safeLocalStorage.getItem('googleSignInRole') as UserRole) || 'participant';
        safeLocalStorage.removeItem('googleSignInRole');

        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        if (!userDoc.exists()) {
            await createUserProfile(result.user, result.user.displayName || 'User', role);
        }

        return result;
    } catch (error: any) {
        console.error('Error handling Google redirect:', error);
        return null;
    }
};

/**
 * Send magic link (email link) for passwordless authentication
 */
export const sendMagicLink = async (
    email: string,
    role: UserRole = 'participant'
): Promise<void> => {

    try {
        const actionCodeSettings = {
            url: `${window.location.origin}/auth/verify`,
            handleCodeInApp: true,
        };

        await sendSignInLinkToEmail(auth, email, actionCodeSettings);

        // Save email and role to localStorage for verification
        safeLocalStorage.setItem('emailForSignIn', email);
        safeLocalStorage.setItem('roleForSignIn', role);
    } catch (error: any) {
        console.error('Error sending magic link:', error);
        throw new Error(getAuthErrorMessage(error.code));
    }
};

/**
 * Complete magic link sign-in
 */
export const completeMagicLinkSignIn = async (): Promise<UserCredential> => {

    try {
        if (!isSignInWithEmailLink(auth, window.location.href)) {
            throw new Error('Invalid sign-in link');
        }

        let email = safeLocalStorage.getItem('emailForSignIn');
        const role = (safeLocalStorage.getItem('roleForSignIn') as UserRole) || 'participant';

        if (!email) {
            email = window.prompt('Please provide your email for confirmation') || '';
        }

        const result = await signInWithEmailLink(auth, email, window.location.href);

        // Check if user profile exists, if not create it
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        if (!userDoc.exists()) {
            await createUserProfile(result.user, email.split('@')[0], role);
        }

        // Clear localStorage
        safeLocalStorage.removeItem('emailForSignIn');
        safeLocalStorage.removeItem('roleForSignIn');

        return result;
    } catch (error: any) {
        console.error('Error completing magic link sign-in:', error);
        throw new Error(getAuthErrorMessage(error.code));
    }
};

/**
 * Sign out current user
 */
export const signOut = async (): Promise<void> => {

    try {
        await firebaseSignOut(auth);
    } catch (error: any) {
        console.error('Error signing out:', error);
        throw new Error('Failed to sign out');
    }
};

/**
 * Get current user profile from Firestore
 */
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {

    try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
            return userDoc.data() as UserProfile;
        }
        return null;
    } catch (error) {
        console.error('Error getting user profile:', error);
        return null;
    }
};

/**
 * Create user profile in Firestore
 */
const createUserProfile = async (
    user: User,
    displayName: string,
    role: UserRole
): Promise<void> => {
    const userProfile: UserProfile = {
        uid: user.uid,
        email: user.email || '',
        displayName: displayName || user.displayName || 'User',
        role,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        profileComplete: false, // New users need to complete their profile
        organizationId: '', // Set by provisionUser Cloud Function after superadmin provisions the user
    };

    await setDoc(doc(db, 'users', user.uid), userProfile);
};

/**
 * Update user profile
 */
export const updateUserProfile = async (
    uid: string,
    updates: Partial<UserProfile>
): Promise<void> => {
    // We could mock this too, but for now we haven't implemented update in mockAuth

    try {
        await updateDoc(
            doc(db, 'users', uid),
            { ...updates, updatedAt: serverTimestamp() }
        );
    } catch (error) {
        console.error('Error updating user profile:', error);
        throw new Error('Failed to update profile');
    }
};

/**
 * Re-send the verification email for the signed-in user.
 */
export const resendVerificationEmail = async (): Promise<void> => {
    if (!auth.currentUser) throw new Error('Not signed in');
    await sendEmailVerification(auth.currentUser);
};

/**
 * Subscribe to auth state changes
 */
export const onAuthStateChange = (callback: (user: User | null) => void) => {

    return onAuthStateChanged(auth, callback);
};

/**
 * Get current authenticated user
 */
export const getCurrentUser = (): User | null => {

    return auth.currentUser;
};

/**
 * Get friendly error message from Firebase auth error code
 */
const getAuthErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
        case 'auth/email-already-in-use':
            return 'This email is already registered. Please sign in instead.';
        case 'auth/invalid-email':
            return 'Invalid email address.';
        case 'auth/operation-not-allowed':
            return 'This sign-in method is not enabled. Please contact support.';
        case 'auth/weak-password':
            return 'Password is too weak. Please use at least 6 characters.';
        case 'auth/user-disabled':
            return 'This account has been disabled.';
        case 'auth/user-not-found':
            return 'No account found with this email.';
        case 'auth/wrong-password':
            return 'Incorrect password.';
        case 'auth/invalid-credential':
            return 'Invalid email or password.';
        case 'auth/popup-closed-by-user':
            return 'Sign-in popup was closed. Please try again.';
        case 'auth/cancelled-popup-request':
            return 'Sign-in was cancelled.';
        case 'auth/network-request-failed':
            return 'Network error. Please check your connection.';
        case 'auth/too-many-requests':
            return 'Too many attempts. Please wait a few minutes and try again.';
        case 'auth/missing-email':
            return 'Please enter your email address.';
        default:
            return 'Something went wrong. Please try again.';
    }
};
